import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, RadioGroup, Alert, Spinner } from '@glplus/glpweb-dsl';
import { vendorApiClient, type ProductBlock, type ProductQuestion, type ReopenInput, type SentProduct } from '../../lib/vendorApiClient';
import { checkBarcode } from '../../lib/addProductForm';
import { notifyProductsChanged } from '../../lib/productEvents';
import { BORDER, HEADING, MUTED, ORANGE_INK, RED, FieldLabel, ProductThumb, departmentPath, describeProductError } from './parts';
import { QuestionActions, outcomeNotice, ourBarcodeKey, reasonKey, sentSource, suggestedTitle } from './questionParts';
import MissingInfoForm from './MissingInfo';

/**
 * Why a product is hidden at every location, and the way out of it.
 *
 *   Q2 (Figma 64:89)  — our question: what you sent beside what we have, yes / no.
 *   Q4 (Figma 65:269) — it is with us; the vendor can take it back.
 *   Q3 (Figma 65:154) — we are stuck; telling us more reopens it (§15q, A7).
 */

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

export default function BlockPanel({
  vendorId,
  vendorProductId,
  block,
  canResolve,
  language,
  onChanged,
}: {
  vendorId: string;
  vendorProductId: string;
  block: ProductBlock;
  canResolve: boolean;
  language: string;
  /** Something was answered, handed over, taken back or reopened. */
  onChanged: (notice: string) => void;
}) {
  const { t } = useTranslation();
  const day = (iso: string) => new Date(iso).toLocaleDateString(language.startsWith('es') ? 'es-US' : 'en-US', { month: 'short', day: 'numeric' });
  const name = block.sent?.name ?? '';
  const missing = block.missing ?? [];

  return (
    <Box data-testid={`product-block-${block.state}`}>
      {block.state === 'questioned' && (
        <>
          <Alert severity="warning" title={t('products.block.questioned.title')} sx={{ mt: 2 }}>
            {t(missing.length > 0 ? 'products.missing.blockBody' : 'products.block.questioned.body')}
          </Alert>
          {missing.length > 0 && (
            <Card>
              <Typography component="h2" sx={{ fontSize: 19, fontWeight: 700, color: HEADING }}>
                {t('products.missing.title', { name })}
              </Typography>
              <MissingInfoForm
                key={missing.join('|')}
                vendorId={vendorId}
                vendorProductId={vendorProductId}
                missing={missing}
                canResolve={canResolve}
                onDone={(result) => onChanged(t(result.remaining.length === 0 ? 'products.questions.done.confirmed' : 'products.missing.savedMore', { name }))}
              />
            </Card>
          )}
          {block.questions.filter((q) => q.kind !== 'missing_field').map((q) =>
            q.suggested && block.sent ? (
              <MatchQuestion
                key={q.questionId}
                vendorId={vendorId}
                question={q}
                sent={block.sent}
                canResolve={canResolve}
                onDone={(outcome) => onChanged(outcomeNotice(t, name, outcome))}
              />
            ) : (
              <Card key={q.questionId}>
                <Typography sx={{ fontSize: 15, color: HEADING }}>{t('products.block.questioned.other')}</Typography>
              </Card>
            ),
          )}
        </>
      )}

      {block.state === 'handed_to_glp' && (
        <WithUs
          since={block.questions.find((q) => q.handedAt)?.handedAt ?? null}
          day={day}
          canResolve={canResolve}
          takeBack={async () => {
            await vendorApiClient.takeBackProduct(vendorId, vendorProductId);
            notifyProductsChanged();
            onChanged(t('products.block.withUs.takenBack'));
          }}
        />
      )}

      {block.state === 'unresolvable' && (
        <Stuck
          vendorId={vendorId}
          question={block.questions[0] ?? null}
          day={day}
          canResolve={canResolve}
          reopen={async (input) => {
            await vendorApiClient.reopenProduct(vendorId, vendorProductId, input);
            notifyProductsChanged();
            onChanged(t('products.block.stuck.reopened'));
          }}
        />
      )}

      {block.history.length > 0 && block.state !== 'questioned' && (
        <Card>
          <Typography component="h2" sx={{ fontSize: 19, fontWeight: 600, color: HEADING }}>
            {t('products.block.history.title')}
          </Typography>
          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }} data-testid="product-block-history">
            {block.history.map((h, i) => (
              <Box component="li" key={`${h.at}-${h.event}-${i}`} sx={{ display: 'flex', gap: 2, py: 0.75 }}>
                <Typography sx={{ width: 64, flexShrink: 0, fontSize: 13, color: MUTED }}>{day(h.at)}</Typography>
                <Typography sx={{ fontSize: 15, color: HEADING }}>
                  {h.event === 'asked'
                    ? block.sent
                      ? t('products.block.history.asked', { source: sentSource(t, block.sent) })
                      : t('products.block.history.askedPlain')
                    : t(`products.block.history.${h.event}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>
      )}
    </Box>
  );
}

function Card({ children, tone }: { children: React.ReactNode; tone?: 'red' }) {
  return (
    <Box sx={{ mt: 2, p: 3, bgcolor: '#fff', border: tone === 'red' ? `2px solid ${RED}` : `1px solid ${BORDER}`, borderRadius: '10px' }}>
      {children}
    </Box>
  );
}

// ─── Q2: is this the same product? ───────────────────────────────

function same(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

function MatchQuestion({
  vendorId,
  question,
  sent,
  canResolve,
  onDone,
}: {
  vendorId: string;
  question: ProductQuestion;
  sent: SentProduct;
  canResolve: boolean;
  onDone: React.ComponentProps<typeof QuestionActions>['onDone'];
}) {
  const { t, i18n } = useTranslation();
  const suggested = question.suggested;
  if (!suggested) return null;
  const reason = reasonKey(question);
  const none = t('products.block.compare.noneSent');
  const dept = (d: string | null, c: string | null) => departmentPath(t, i18n.language, d, c);

  const rows: { key: string; sent: string; ours: string; differs?: boolean }[] = [
    {
      key: 'photo',
      sent: t(sent.hasPhoto ? 'products.block.compare.photoSent' : 'products.block.compare.noneSent'),
      ours: t(suggested.imageUrl ? 'products.block.compare.ourPhoto' : 'products.block.compare.noPhoto'),
    },
    { key: 'name', sent: sent.name, ours: suggested.name, differs: !same(sent.name, suggested.name) },
    { key: 'brand', sent: t('products.block.compare.none'), ours: suggested.brand ?? t('products.block.compare.none') },
    {
      key: 'barcode',
      sent: sent.barcode ?? t('products.barcode.none'),
      // Ours is never shown. When the two match, the vendor is looking at it already.
      ours: t(`products.block.compare.ourBarcode.${ourBarcodeKey(question)}`),
    },
    { key: 'size', sent: sent.size ?? none, ours: suggested.size ?? t('products.block.compare.none'), differs: !!sent.size && !!suggested.size && !same(sent.size, suggested.size) },
    {
      key: 'department',
      sent: dept(sent.department, sent.category) || none,
      ours: dept(suggested.department, suggested.category) || t('products.block.compare.none'),
      differs: !!sent.department && !!suggested.department && !same(sent.department, suggested.department),
    },
    { key: 'ingredients', sent: sent.ingredients ?? none, ours: suggested.ingredients ?? t('products.block.compare.none') },
  ];

  return (
    <Card>
      <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }} data-testid="product-question-title">
        {t('products.block.question.title', { product: suggestedTitle(suggested) })}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 15, color: '#343434' }}>{t(`products.block.question.why.${reason}`)}</Typography>
      {reason === 'staff_proposal' && question.note && (
        <Typography sx={{ mt: 1, fontSize: 15, color: HEADING }} data-testid="product-question-note">
          {t('products.questions.fromUsNote', { note: question.note })}
        </Typography>
      )}

      <Box role="table" aria-label={t('products.block.compare.aria')} sx={{ mt: 2, border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: 2, px: 2, py: 1.25, bgcolor: '#F7F7F8', borderBottom: `1px solid ${BORDER}` }}>
          <span role="columnheader" />
          <Box role="columnheader">
            <FieldLabel>{t('products.questions.youSent')}</FieldLabel>
          </Box>
          <Box role="columnheader">
            <FieldLabel>{t('products.block.compare.weHave')}</FieldLabel>
          </Box>
        </Box>
        {rows.map((r) => (
          <Box
            role="row"
            key={r.key}
            data-testid={`product-compare-${r.key}`}
            sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: 2, px: 2, py: 1.5, alignItems: 'center', borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}
          >
            <Box role="rowheader">
              <FieldLabel>{t(`products.block.compare.row.${r.key}`)}</FieldLabel>
            </Box>
            <Box role="cell">
              <Typography sx={{ fontSize: 15, color: r.differs ? ORANGE_INK : HEADING, whiteSpace: 'pre-wrap' }}>{r.sent}</Typography>
              {r.differs && <Typography sx={{ fontSize: 13, fontWeight: 700, color: ORANGE_INK }}>{t('products.block.compare.different')}</Typography>}
            </Box>
            <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {r.key === 'photo' && suggested.imageUrl && <ProductThumb src={suggested.imageUrl} size={44} />}
              <Typography sx={{ fontSize: 15, color: HEADING, whiteSpace: 'pre-wrap' }}>{r.ours}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <QuestionActions vendorId={vendorId} question={question} canResolve={canResolve} onDone={onDone} />

      <Box sx={{ mt: 2.5, p: 2, bgcolor: '#F7F7F8', borderRadius: '8px', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {(['yes', 'no', 'handOff'] as const).map((k) => (
          <Box key={k}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: HEADING }}>{t(`products.block.explain.${k}.title`)}</Typography>
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              {t(k === 'no' ? `products.block.explain.no.${reason}` : `products.block.explain.${k}.body`, { product: suggestedTitle(suggested) })}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

// ─── Q4: it is with us ───────────────────────────────────────────

function WithUs({
  since,
  day,
  canResolve,
  takeBack,
}: {
  since: string | null;
  day: (iso: string) => string;
  canResolve: boolean;
  takeBack: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card>
      <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
        {t('products.block.withUs.title')}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 15, color: '#343434' }}>
        {since ? t('products.block.withUs.bodySince', { date: day(since) }) : t('products.block.withUs.body')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {canResolve && (
        <Box sx={{ mt: 2 }}>
          <Button
            ariaLabel={t('products.block.withUs.takeBack')}
            buttonText={t('products.block.withUs.takeBack')}
            variant="outlinedGreen"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              takeBack()
                .catch((e: unknown) => setError(describeProductError(e, t)))
                .finally(() => setBusy(false));
            }}
            dataTestId="product-take-back"
          />
        </Box>
      )}
    </Card>
  );
}

// ─── Q3: we are stuck ────────────────────────────────────────────

function Stuck({
  vendorId,
  question,
  day,
  canResolve,
  reopen,
}: {
  vendorId: string;
  question: ProductQuestion | null;
  day: (iso: string) => string;
  canResolve: boolean;
  reopen: (input: ReopenInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [hasBarcode, setHasBarcode] = useState<'' | 'value' | 'none'>('');
  const [barcode, setBarcode] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) return setError(t('products.errors.imageTooLarge'));
    setUploading(true);
    setError(null);
    try {
      const { cdnUrl } = await vendorApiClient.uploadAsset(vendorId, 'product-photo', file);
      setPhotoUrl(cdnUrl);
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setUploading(false);
    }
  };

  const hasSomething = hasBarcode === 'none' || (hasBarcode === 'value' && barcode.trim() !== '') || !!photoUrl || note.trim() !== '';

  const send = () => {
    const input: ReopenInput = {};
    if (hasBarcode === 'none') input.barcode = { kind: 'none' };
    if (hasBarcode === 'value') {
      const checked = checkBarcode(barcode);
      if (!checked.ok) return setBarcodeError(t(`products.errors.${checked.problem}`));
      input.barcode = { kind: 'value', value: checked.code };
    }
    if (photoUrl) input.photoUrl = photoUrl;
    if (note.trim()) input.note = note.trim();
    setBarcodeError(null);
    setBusy(true);
    setError(null);
    reopen(input)
      .catch((e: unknown) => setError(describeProductError(e, t)))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Card tone="red">
        <Typography component="h2" sx={{ fontSize: 19, fontWeight: 700, color: RED }}>
          {t('products.block.stuck.title')}
        </Typography>
        {question?.stuckReason && (
          <Typography sx={{ mt: 1, fontSize: 15, color: HEADING }} data-testid="product-stuck-reason">
            {question.stuckAt
              ? t('products.block.stuck.fromUs', { date: day(question.stuckAt), reason: question.stuckReason })
              : question.stuckReason}
          </Typography>
        )}
        <Typography sx={{ mt: 1, fontSize: 13, color: MUTED }}>{t(canResolve ? 'products.block.stuck.hint' : 'products.block.stuck.hintReadOnly')}</Typography>
      </Card>

      {canResolve && (
        <Card>
          <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
            {t('products.block.stuck.formTitle')}
          </Typography>
          <Box sx={{ mt: 1.5 }} data-testid="product-reopen-barcode-choice">
            <RadioGroup
              ariaLabel={t('products.block.stuck.barcodeQuestion')}
              name="reopen-barcode"
              row
              value={hasBarcode}
              onChange={(e) => {
                setHasBarcode(e.target.value as 'value' | 'none');
                setBarcodeError(null);
              }}
              options={[
                { value: 'value', label: t('products.block.stuck.hasBarcode') },
                { value: 'none', label: t('products.block.stuck.noBarcode') },
              ]}
            />
          </Box>
          {hasBarcode === 'value' && (
            <Box sx={{ mt: 1, maxWidth: 320 }}>
              <TextField
                ariaLabel={t('products.block.stuck.barcodeLabel')}
                label={t('products.block.stuck.barcodeLabel')}
                value={barcode}
                onChange={(e) => {
                  setBarcode(e.target.value);
                  setBarcodeError(null);
                }}
                error={!!barcodeError}
                errorText={barcodeError ?? undefined}
                disabled={busy}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 20 } }}
                dataTestId="product-reopen-barcode"
              />
            </Box>
          )}

          <Box sx={{ mt: 2, p: 2, border: `1px dashed #9AA3AD`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t('products.block.stuck.photoTitle')}</Typography>
              <Typography sx={{ fontSize: 13, color: MUTED }}>{t(photoUrl ? 'products.request.attach.added' : 'products.block.stuck.photoHint')}</Typography>
            </Box>
            {photoUrl && <Box component="img" src={photoUrl} alt={t('products.request.attach.previewAlt')} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px' }} />}
            {uploading ? (
              <Spinner color="brand" />
            ) : (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  data-testid="product-reopen-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void upload(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  ariaLabel={t(photoUrl ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                  buttonText={t(photoUrl ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                  variant="outlinedGreen"
                  disabled={busy}
                  onClick={() => fileInput.current?.click()}
                />
              </>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              ariaLabel={t('products.block.stuck.note')}
              label={t('products.block.stuck.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              disabled={busy}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              dataTestId="product-reopen-note"
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="product-reopen-error">
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              ariaLabel={t('products.block.stuck.send')}
              buttonText={t('products.block.stuck.send')}
              variant="contained"
              color="primary"
              disabled={busy || uploading || !hasSomething}
              onClick={send}
              dataTestId="product-reopen-send"
            />
          </Box>
        </Card>
      )}
    </>
  );
}
